import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MenuContext } from "@lynx/ui-components";
import { Spinner } from "react-bootstrap";

import TableComponent from "ui/ui-kit/react-table/TableComponent";
import constants from "lib/constants";
import { accountThunks } from "actions-thunks/account-thunks";
import { requestsThunks } from "actions-thunks/requests-thunks";
import { requestTableUtils } from "ui/utilities/requestsTableUtils";
import { isVisibleRequestInOverviewTables } from "ui/utilities/requestVisibility";
import { ModalContext } from "ui/ui-kit/modal/ModalContext";
import SupportStateController from "./state-controller/SupportStateController";
import { NewRequest } from "ui/account-overview/requests/NewRequest";
import useSocketNamespace from "ui/hooks/useSocket";
import useStatistic from "ui/hooks/useStatistic";
import { supportTableUtils } from "ui/ui-kit/react-table/supportTableUtils";

import "./SupportPage.scss";

const { tableFormattedData, setCellClassName, showInOverlay } = requestTableUtils;
const { RequestTypes } = constants;

const SupportPage = () => {
  const { currentAccount } = useSelector((state) => state.accounts);
  const { isInitializedSocket } = useSelector((state) => state.sockets);
  const { requestsItems, riskSettingMetadata } = useSelector(
    (state) => state.requests
  );
  const { showModal, hideModal } = useContext(ModalContext);
  const requestsItemsDataALL = requestsItems?.[RequestTypes.ALL];
  const visibleRequestsItemsDataALL = requestsItemsDataALL?.filter(
    isVisibleRequestInOverviewTables
  );
  const [visibleItems, setVisibleItems] = useState(
    visibleRequestsItemsDataALL?.length > 10
      ? 10
      : visibleRequestsItemsDataALL?.length
  );
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.utils);

  useSocketNamespace(currentAccount, 'applications', isInitializedSocket);
  useStatistic(currentAccount?._id, "Open page Support");

  useEffect(() => {
    const total = visibleRequestsItemsDataALL?.length ?? 0;
    setVisibleItems((prev) => {
      if (total < (prev ?? 0)) return total;
      if (prev === 0 || prev === undefined) return total > 10 ? 10 : total;
      return prev;
    });
  }, [visibleRequestsItemsDataALL?.length]);

  useEffect(() => {
    if (currentAccount && !!Object.keys(currentAccount).length) {
      dispatch(accountThunks.setAccountExtensionsTC(currentAccount._id));
      dispatch(requestsThunks.getRiskSettingsMetadataTC(currentAccount._id));
      dispatch(requestsThunks.getAccountRequestsTC({
        accID: currentAccount._id,
        sort: "updatedAt",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount]);

  const renderNoDataBlock = () => {
    return (
      <div className="support-no-data-block">
        <span>No active requests</span>
      </div>
    );
  };

  const cancelRequest = (data) => {
    const id = visibleRequestsItemsDataALL?.find(
      (r) => r.sequenceNumber === data.original.id
    )?._id;
    dispatch(
      requestsThunks.updateRequestStatusTC({
        accID: currentAccount._id,
        requestID: id,
        action: "cancel"
      })
    );
  };

  const showInOverlayHandler = (row, isLastItem) => {
    return showInOverlay({
      row,
      isLastItem,
      showModal,
      hideModal,
      cancelRequest,
      MenuContext,
      requestsItemsDataALL: visibleRequestsItemsDataALL,
    });
  };

  return (
    <div className="support-wrapper">
      {loading && <Spinner className="spinner" animation="grow" />}
      {visibleRequestsItemsDataALL && (
        <div className="support-table">
          <div className="support-table-header">
            <NewRequest />
          </div>
          <div className="support-table-body">
            <TableComponent
              data={tableFormattedData(
                false,
                visibleRequestsItemsDataALL.slice(-visibleItems),
                riskSettingMetadata,
                currentAccount
              )}
              dateColumns={["created", "updated"]}
              formattingForDateColumns={supportTableUtils.tableTimeFormat}
              showSearch={false}
              requestsItemsDataALL={visibleRequestsItemsDataALL}
              setCellClassName={setCellClassName}
              showRowOverlay={showInOverlayHandler}
              itemsToUseSorting={[
                "id",
                "category",
                "type",
                "state",
                "updated",
                "created",
              ]}
              noDataBlock={renderNoDataBlock()}
            />
          </div>
          <SupportStateController
            setVisibleItems={setVisibleItems}
            visibleItems={visibleItems}
            items={visibleRequestsItemsDataALL}
          />
        </div>
      )}
    </div>
  );
};

export default SupportPage;
